import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// +++ ADDED: FilePond imports +++
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
// import 'filepond-plugin-pdf-preview/dist/filepond-plugin-pdf-preview.min.css';
// +++ ADDED: Import FilePond plugins +++
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

import {
  Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label,
  CardHeader, Alert, Spinner, Input, FormFeedback
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; 
import { fetchSubCategories, updateDocument, uploadDocumentFile } from '../../../slices/AIDoc/thunk';
import { fetchParentCategories } from '../../../slices/thunks';
import { clearUploadedDocumentResponse, clearUploadError } from '../../../slices/AIDoc/reducer';

import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import { getAuthUser } from '../../../helpers/utils';


// +++ ADDED: Register FilePond plugins +++
registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
);



// --- Custom Styles for React-Select ---
// const customSelectStyles = { // Step 3 commented out
//   control: (provided) => ({
//     ...provided,
//   }),
//   multiValueLabel: (provided) => ({
//     ...provided,
//     color: 'white',
//   }),
//   input: (provided) => ({
//     ...provided,
//     color: 'white',
//   }),
//   placeholder: (provided) => ({
//     ...provided,
//     // color: 'rgba(255, 255, 255, 0.7)', 
//   }),
// };

export const MAX_PAGES = 10;
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

// --- Main Component ---
const DocumentUploadPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = getAuthUser();

  const isRTL = i18n.dir() === "rtl";

  const [filepondFiles, setFilepondFiles] = useState([]); 
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  // const [isSaving, setIsSaving] = useState(false); // No longer needed with programmatic save
  const [isSaved, setIsSaved] = useState(false); 
  const [processingLog, setProcessingLog] = useState([]);
  const [SubCategories, setSubCategories] = useState([]);
  const [activeStep, setActiveStep] = useState(1); 
  // const [isTagCreating, setIsTagCreating] = useState(false); // Step 3 commented out
  // const [allTags, setAllTags] = useState([]); // Step 3 commented out
  // const [tagInputValue, setTagInputValue] = useState(''); // Step 3 commented out
  // const [previewUrl, setPreviewUrl] = useState(null); // PDF Preview commented out
  const [isGeneratingDetails, setIsGeneratingDetails] = useState(false);
  const [isFileValid, setIsFileValid] = useState(false);

  const [isMultiUpload, setIsMultiUpload] = useState(true);
  const [processedFiles, setProcessedFiles] = useState([]);


  const filepondRef = useRef(null);
  const completedFilesCount = useRef(0);
  const activeFileId = useRef(null); 

  // --- REDUX STATE ---
  const {
    documentUploadLoading,
    uploadedDocumentResponse,
    documentUploadError,
    loading: categoriesLoading,
    subCategories,
    subCategoriesLoading,
    metadataSubmissionLoading,
    uploadProgress,
    savedDocumentMetadata,  
  } = useSelector((state) => state.AIDoc);

  const parentCategories = useSelector((state) => state.Category.parentCategories);
  

  // --- FORMIK & YUP INTEGRATION ---
  const validationSchema = Yup.object().shape({
    selectedCategory: Yup.object().nullable().required(t("Category is required")),
    selectedSubCategory: Yup.object().nullable().required(t("Sub-category is required")),
    // --- Step 3 fields commented out ---
    // docName: Yup.string().when('isMultiUpload', {
    //   is: false,
    //   then: (schema) => schema.trim().min(2, t("Document name must be at least 3 characters")).max(100, t("MaxxDocNameCharactersError")).required(t("DocumentRequired")),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // title: Yup.string().when('isMultiUpload', {
    //   is: false,
    //   then: (schema) => schema.trim().min(3, t("Title must be at least 3 characters")).max(100, t("MaxxCharactersError")).required(t("Title is required")),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // docDescription: Yup.string().when('isMultiUpload', {
    //   is: false,
    //   then: (schema) => schema.trim().min(10, t("Description must be at least 10 characters")).max(700, t("MaxxDescCharactersError")).required(t("DescriptionRequired")),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // selectedTags: Yup.array().when('isMultiUpload', {
    //   is: false,
    //   then: (schema) => schema.min(1, t("PleaseOneTag")).required(t("Tags are required")),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  });
  
  const formik = useFormik({
    initialValues: {
        selectedCategory: null,
        selectedSubCategory: null,
        // --- Step 3 fields commented out ---
        // docName: '',
        // title: '',
        // docDescription: '',
        // selectedTags: [],
    },
    validationSchema: validationSchema,
    // --- onSubmit is no longer needed as saving is programmatic ---
    onSubmit: async (values) => {
        // This logic is now handled inside handleAnalyze
        console.log("Form submission is disabled. Saving is handled by the Analyze process.");
    },
  });

  const areCategoriesSelected = formik.values.selectedCategory && formik.values.selectedSubCategory;

  const handleClear = useCallback(() => {
    setActiveStep(1);
    if (filepondRef.current) {
        filepondRef.current.removeFiles();
    }
    setIsFileValid(false);
    setFilepondFiles([]);
    setIsAnalyzed(false);
    setIsSaved(false);
    // setIsSaving(false);
    setProcessingLog([]);
    setIsGeneratingDetails(false);
    dispatch(clearUploadedDocumentResponse());
    formik.resetForm();
    setIsMultiUpload(true);
    setProcessedFiles([]);
    completedFilesCount.current = 0;
  }, [dispatch, formik]);

  // Handler for creating a new tag - Commented out as Step 3 is removed
  // const handleCreateTag = async (inputValue) => {
  //   setIsTagCreating(true);
  //   try {
  //     const newTag = await dispatch(addNewTag({ TagName: inputValue })).unwrap();
  //     const newOption = { label: newTag.TagName, value: newTag.ID };
  //     setAllTags(prev => [...prev, newOption]);
  //     formik.setFieldValue('selectedTags', [...formik.values.selectedTags, newOption]);
  //   } catch (e) {
  //     toast.error(t("Failed to create tag"));
  //   } finally {
  //     setIsTagCreating(false);
  //   }
  // };

  const handleFilePondAddFile = async (error, fileItem) => {
    if (error) return;

    const file = fileItem.file;
    if (file.type === "application/pdf") {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

            if (pdf.numPages > MAX_PAGES) {
                toast.error(t(`PDF exceeds 10 pages.`));

                if (filepondRef.current) {
                    filepondRef.current.removeFile(fileItem.id);
                }
            }
        } catch (err) {
            console.error("PDF validation failed:", err);
            toast.error(t("Unable to validate PDF pages."));

            if (filepondRef.current) {
                filepondRef.current.removeFile(fileItem.id);
            }
        }
    }
  };


  // +++ MODIFIED: Simplified file update handler, relies on FilePond validation +++
  const handleFilePondUpdate = (fileItems) => {
      setFilepondFiles(fileItems);
      const currentTotalSize = fileItems.reduce((total, item) => total + (item.file ? item.file.size : 0), 0);
      const isTotalSizeOk = currentTotalSize <= MAX_FILE_SIZE;
      const hasOtherPermanentErrors = fileItems.some(item => 
          item.error && item.error !== filepondRef.current?.props.labelMaxTotalFileSizeExceeded
      );
      const allFilesAreCurrentlyValid = fileItems.length > 0 && isTotalSizeOk && !hasOtherPermanentErrors;
      
      setIsFileValid(allFilesAreCurrentlyValid);

  };

  // +++ ADDED: Handler for FilePond warnings (like total size exceeded) +++
  const handleFilePondWarning = (error, file, status) => {
    if (error.body === 'Total file size has been exceeded') {
        const message = isRTL 
            ? `لقد تم تجاوز الحجم الإجمالي للملفات. الحد الأقصى هو ${MAX_FILE_SIZE / (1024*1024)} ميجا بايت.`
            : `Total file size exceeded. The limit is ${MAX_FILE_SIZE / (1024*1024)} MB.`;
        toast.warn(message); 
        return false; 
    }
    return true;
};



const handleAnalyze = () => {
    if (!filepondFiles.length || !areCategoriesSelected) {
        toast.error(t("Cannot analyze. File(s) or category is missing."));
        return;
    }

    dispatch(clearUploadError());
    setProcessingLog([]);
    setIsGeneratingDetails(true);

    activeFileId.current = null;

    const initialFiles = filepondFiles.map(fpFile => ({
        id: fpFile.id,
        name: fpFile.filename,
        status: 'pending',
        message: 'WaitingInQueue',
    }));
    setProcessedFiles(initialFiles);

    // No more checkAndFinalize function needed here.

    const onProgress = ({ event, data }) => {
        try {
            const parsedData = typeof data === "string" ? JSON.parse(data) : data;
            
            if (event === "init") {
                setProcessedFiles(prev => {
                    const nextFileIndex = prev.findIndex(f => f.status === 'pending');
                    if (nextFileIndex > -1) {
                        activeFileId.current = prev[nextFileIndex].id;
                        return prev.map((file, index) => 
                            index === nextFileIndex 
                                ? { ...file, status: 'analyzing', message: parsedData.message || 'Starting analysis...' } 
                                : file
                        );
                    }
                    return prev;
                });
                return;
            }

            if (event === "error") {
                if (activeFileId.current) {
                    setProcessedFiles(prev => prev.map(f =>
                        f.id === activeFileId.current ? { ...f, status: 'error', message: parsedData.message } : f
                    ));
                }
                return;
            }
            
            if (event === "duplicate") {
                if (activeFileId.current) {
                    setProcessedFiles(prev => prev.map(f => 
                     f.id === activeFileId.current
                        ? { ...f, status: 'error', message: parsedData.message || t('duplicateFile') }
                        : f
                    ));
                }
                return;
            }

            if (event === "file_uploaded") {
                if (activeFileId.current) {
                    setProcessedFiles(prev => prev.map(f => 
                     f.id === activeFileId.current
                        ? { ...f, message: parsedData.message }
                        : f
                    ));
                }
                return;
            }

            if (event === "file_ocr_done") {
                const data = {
                    DocumentID: parsedData.DBID,
                    OriginFileName: parsedData.text?.originalFileName,   
                    Tags: parsedData.text?.tags
                }
                dispatch(updateDocument(data))
                .unwrap()
                .then(() => {
                    navigate(`/ai/document`);
                })
                .catch((error) => {
                    console.error("Metadata save failed:", error);
                });

                // if (activeFileId.current) {
                //     setProcessedFiles(prev => prev.map(f => 
                //      f.id === activeFileId.current
                //         ? { ...f, message: parsedData.message }
                //         : f
                //     ));
                // }
                // return;
            }

            if (event === "file_processed") {
                // if (activeFileId.current) {
                //     const fileIdToUpdate = activeFileId.current;
                //     activeFileId.current = null; 
                //     const payload = {
                //         DocumentTypeID: 4, 
                //         FileName: parsedData?.book,
                //         HashName: parsedData?.filename,
                //         OriginFileName: parsedData?.originalFileName,
                //         FileTitle: parsedData?.title, FileDesc: parsedData?.description,
                //         FileType: 'application/pdf', FileURL: parsedData?.pdfUrl,
                //         CategoryID: formik.values.selectedCategory.value, SubCategoryID: formik.values.selectedSubCategory.value,
                //         UniqueID: parsedData?.uniqueId || null, tagNames: parsedData?.tags || [],
                //         JsonURL: parsedData.url
                //     };
    
              
            }
        } catch (error) {
            console.error("Failed to process stream event:", error, data);
            setIsGeneratingDetails(false);
        }
    };

    const formData = new FormData();
    filepondFiles.forEach(fpFile => {
      formData.append("file", fpFile.file); 
    });
    formData.append("IsBook", false);
    formData.append("UserID", user.ID);
    formData.append("DocumentTypeID", 4);
    formData.append("Category", formik.values.selectedCategory?.Category || "");
    formData.append("SubCategory", formik.values.selectedSubCategory?.SubCategory || "");
    formData.append("CategoryID", formik.values.selectedCategory?.value || "");
    formData.append("SubCategoryID", formik.values.selectedSubCategory?.value || "");
    formData.append("IsLargeFile", false);
    formData.append("SaveJSON", false);

    dispatch(uploadDocumentFile({ formData, onProgress, isMultiUpload: filepondFiles.length > 1 }));
};

const handleCategorySelect = async (category) => {
  formik.setFieldValue('selectedCategory', category);
  formik.setFieldValue('selectedSubCategory', null);
  setIsAnalyzed(false);

  if (filepondRef.current) {
    filepondRef.current.removeFiles();
  }

  if (category) {
    try {
      const result = await dispatch(fetchSubCategories({ DocType: 4, ParentCategoryID: category.value })).unwrap();
      setSubCategories(result || []);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubCategories([]);
    }
  } else {
    setSubCategories([]);
  }
};


  useEffect(() => {
    dispatch(fetchParentCategories({ DocType: 4 })); 
  }, [dispatch]);

  useEffect(() => {
    return () => {
      handleClear();
      dispatch(clearUploadedDocumentResponse());  
    };
  }, []);

  useEffect(() => {
        // Only run this check if we are currently in the processing phase.
        if (!isGeneratingDetails) {
        return;
        }
        // Count how many files are "finished" (either success or error).
        const finishedCount = processedFiles.filter(
            f => f.status === 'success' || f.status === 'error'
        ).length;

        // Check if all files have been processed and the list is not empty.
        if (finishedCount > 0 && finishedCount === filepondFiles.length) {
            setIsGeneratingDetails(false);
            setIsAnalyzed(true);

            // Count successes to decide if we show the summary screen.
            // const successes = processedFiles.filter(f => f.status === 'success').length;

            setIsSaved(true);
        }
    }, [processedFiles, isGeneratingDetails, filepondFiles.length]);




  // Fetch all tags useEffect commented out
  // useEffect(() => {
  //   const fetchTags = async () => {
  //     try {
  //       const tags = await dispatch(fetchAllTags()).unwrap();
  //       setAllTags(tags.map(tag => ({ label: tag.TagName.trim(), value: tag.ID })));
  //     } catch (e) {
  //       setAllTags([]);
  //     }
  //   };
  //   fetchTags();
  // }, [dispatch, isTagCreating]);
  
  
  // --- Options for Select components ---
  const ParentCategories = (parentCategories || []).map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.NameAr?.trim() : cat.NameEn?.trim(),
    Category: cat.NameEn?.trim() || '',
  }));
  
  const subCategoriesOptions = (subCategories || []).map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.CategoryNameAr?.trim() : cat.CategoryNameEn?.trim(),
    SubCategory: cat.CategoryNameEn?.trim() || '',
  }));

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
            <h4 className="mb-4">{t("Document Upload")}</h4>
            <Card>
                <CardHeader className="d-flex align-items-center">
                    {/* <Button color="secondary" disabled={documentUploadLoading} className="me-2" onClick={() => navigate(-1)}> */}
                    <Button color="secondary" className="me-2" onClick={() => navigate(-1)}>
                        <i className={`${!isRTL ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                    </Button>
                    <h5 className="card-title mb-0 flex-grow-1 me-1">{t("Document Processor")}</h5>
                </CardHeader>
                <CardBody>
                    {isSaved ? (
                       <div className="text-center py-4">
                            <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '4.5rem' }}></i>
                            <h4 className="mt-3">{t("Processing Complete")}</h4>

                            <div className="summary-report mx-auto mt-4" style={{ maxWidth: '600px', textAlign: 'left' }}>
                                {/* Filter and display successful files */}
                                {processedFiles.filter(f => f.status === 'success').length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="mb-3">{t('SuccessfulUploads')} ({processedFiles.filter(f => f.status === 'success').length})</h5>
                                        <div className="list-group">
                                            {processedFiles.filter(f => f.status === 'success').map(file => (
                                                <div key={file.id} className="list-group-item list-group-item-action d-flex align-items-center">
                                                    <i className="ri-checkbox-circle-fill text-success fs-5 me-3"></i>
                                                    <div className="fw-medium">{file.name}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Filter and display failed files */}
                                {processedFiles.filter(f => f.status === 'error').length > 0 && (
                                    <div>
                                        <h5 className="mb-3">{t('FailedUploads')} ({processedFiles.filter(f => f.status === 'error').length})</h5>
                                        <div className="list-group">
                                            {processedFiles.filter(f => f.status === 'error').map(file => (
                                                <div key={file.id} className="list-group-item list-group-item-action d-flex flex-column align-items-start">
                                                    <div className="d-flex align-items-center w-100">
                                                        <i className="ri-close-circle-fill text-danger fs-5 me-3"></i>
                                                        <div className="fw-medium">{file.name}</div>
                                                    </div>
                                                    <div className="ps-4 ms-3 mt-1 text-muted">
                                                        <small>{t('Reason')}: {t(file.message, { defaultValue: file.message })}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-5">
                                <Button color="success" className="me-2 ms-2" onClick={handleClear}>
                                    <i className="ri-add-line align-middle me-1"></i> {t("UploadMoreDocuments")}
                                </Button>

                                <Button color="primary" outline onClick={() => navigate("/ai/document")}>
                                    <i className="ri-file-list-3-line align-middle me-1"></i> {t("View All Documents")}
                                </Button>
                            </div>
                       </div>
                    ) : (
                      <>
                        <div className="stepper-wrapper">
                            <div className={`stepper-item ${activeStep >= 1 ? 'completed' : ''}`}>
                                <div className="step-counter">1</div>
                                <div className="step-name">{t("Classification")}</div>
                            </div>
                            <div className={`stepper-item ${activeStep >= 2 ? 'completed' : ''} ${activeStep === 2 ? 'active' : ''}`}>
                                <div className="step-counter">2</div>
                                <div className="step-name">{t("Upload & Analyze")}</div>
                            </div>
                             {/* Step 3 is now hidden for both single and multi-upload */}
                            {/* {!isMultiUpload && (
                              <div className={`stepper-item ${activeStep === 3 ? 'active' : ''}`}>
                                <div className="step-counter">3</div>
                                <div className="step-name">{t("Add Details")}</div>
                              </div>
                            )} */}
                        </div>
                       <Form onSubmit={formik.handleSubmit}>
                            <Row>
                                 {/* Left COLUMN: METADATA FORM */}
                                <Col lg={5}>
                                    <div className="d-flex flex-column gap-4">
                                        {/* --- STEP 1: CLASSIFICATION --- */}
                                        <div className="step-section">
                                            <h6>{t("Step 1: Choose Classification")}</h6>
                                            <FormGroup className="mb-3 mt-3">
                                                <Label htmlFor="doc-category" className="form-label">{t("Category")}</Label>
                                                <Select
                                                    id="doc-category"
                                                    options={ParentCategories}
                                                    value={formik.values.selectedCategory}
                                                    onChange={handleCategorySelect}
                                                    onBlur={() => formik.setFieldTouched('selectedCategory', true)}
                                                    placeholder={t("select")}
                                                    isLoading={categoriesLoading}
                                                    isDisabled={activeStep > 1} 
                                                    className={formik.touched.selectedCategory && formik.errors.selectedCategory ? 'is-invalid' : ''}
                                                    noOptionsMessage={() => t("NoOptions", { defaultValue: "No categories available" })}
                                                />
                                                {formik.touched.selectedCategory && formik.errors.selectedCategory ? (
                                                    <div className="invalid-feedback d-block">{t(formik.errors.selectedCategory)}</div>
                                                ) : null}
                                            </FormGroup>
                                            <FormGroup className="mb-0">
                                                <Label htmlFor="doc-subcategory" className="form-label">{t("SubCategory")}</Label>
                                                <Select
                                                    id="doc-subcategory"
                                                    options={subCategoriesOptions}
                                                    value={formik.values.selectedSubCategory}
                                                    onChange={(option) => {
                                                        formik.setFieldValue('selectedSubCategory', option);
                                                        setActiveStep(2);
                                                    }}
                                                    onBlur={() => formik.setFieldTouched('selectedSubCategory', true)}
                                                    placeholder={t("select")}
                                                    isLoading={subCategoriesLoading}
                                                    isDisabled={subCategoriesLoading || !formik.values.selectedCategory || activeStep > 1}
                                                    className={formik.touched.selectedSubCategory && formik.errors.selectedSubCategory ? 'is-invalid' : ''}
                                                    noOptionsMessage={() => t("NoOptions", { defaultValue: "No sub-categories available" })}
                                                />
                                                {formik.touched.subCategories && formik.errors.subCategories ? (
                                                    <div className="invalid-feedback d-block">{t(formik.errors.subCategories)}</div>
                                                ) : null}
                                            </FormGroup>
                                        </div>

                                        {/* --- STEP 3: ADD DETAILS (COMMENTED OUT) --- */}
                                        {/*
                                        <div className={`step-section ${activeStep < 3 ? 'disabled' : ''}`}>
                                              <div className="d-flex justify-content-between align-items-center mb-2">
                                                  <h6>{t("Step 3: Add Details")}</h6>
                                                  <Button color="danger" outline size="sm" onClick={handleClear}><i className="ri-restart-line align-middle me-1"></i> {t("Start over")}</Button>
                                              </div>
                                              <fieldset disabled={activeStep < 3}>
                                                  // All FormGroups for name, title, desc, tags were here
                                              </fieldset>
                                              <div className="mt-4">
                                                  <Button color="primary" type="submit" className="w-100"
                                                      disabled={!formik.isValid || !isAnalyzed || isSaving || metadataSubmissionLoading}>
                                                      // Save button logic was here
                                                  </Button>
                                              </div>
                                          </div>
                                        */}
                                        
                                        {/* +++ NEW: Action buttons moved to the left panel for clarity +++ */}
                                        {/* <div className="mt-4 d-flex justify-content-end gap-2">
                                            {(filepondFiles.length > 0) && (
                                                <Button color="danger" outline onClick={handleClear}><i className="ri-restart-line align-middle me-1"></i> {t("Start over")}</Button>
                                            )}
                                        </div> */}
                                    </div>
                                </Col>
                                {/* Right COLUMN: UPLOAD & STATUS */}
                                <Col lg={7}>
                                  <div className={`step-section h-100`}>
                                     <div className="d-flex justify-content-between align-items-center mb-2">
                                        {/* This part is correct, the title stays at the start */}
                                        <h6>{t("Step2FileUpload")}</h6>

                                        {/* This container holds the buttons at the end */}
                                        <div className="d-flex gap-2">
                                            {/* {isFileValid && !isAnalyzed && !documentUploadLoading && !isGeneratingDetails && (filepondFiles.length > 0) && ( */}
                                            {isFileValid && !isAnalyzed && !isGeneratingDetails && (filepondFiles.length > 0) && (
                                                <Button
                                                    color="danger"
                                                    outline
                                                    onClick={handleClear}
                                                    // Add these classes to the button
                                                    className={`d-flex align-items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                >
                                                    <i className="ri-restart-line"></i>
                                                    <span>{t("Start over")}</span>
                                                </Button>
                                            )}
                                            {/* {isFileValid && !isAnalyzed && !documentUploadLoading && !isGeneratingDetails && ( */}
                                            {isFileValid && !isAnalyzed && !isGeneratingDetails && (
                                                <Button
                                                    color="success"
                                                    onClick={handleAnalyze}
                                                    // disabled={documentUploadLoading || isGeneratingDetails}
                                                    disabled={isGeneratingDetails}
                                                    // Add these classes to the button
                                                    className={`d-flex align-items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                >
                                                    <i className="ri-magic-line"></i>
                                                    <span>{t("Analyze Document")}</span>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                      <div className="upload-wrapper border rounded d-flex flex-column justify-content-center p-2" style={{ backgroundColor: '#f8f9fa' }}>
                                          {activeStep < 2 && (
                                              <div className="upload-overlay">
                                                  <i className={` ${isRTL ? 'ri-arrow-right-circle-line' : 'ri-arrow-left-circle-line'}`}></i>
                                                  <h5 className="mb-1">{t("Complete Step 1 First")}</h5>
                                                  <p className="text-muted">{t("Please select a category and sub-category to enable file upload")}</p>
                                              </div>
                                          )}
                                           {isGeneratingDetails ? (
                                                isMultiUpload ? (
                                                    <div className="text-left p-4 w-100">
                                                        <h4 className="mb-3 text-center">{t("AnalyzingDocuments")}</h4>
                                                        <p className="text-muted mb-4 text-center">{t("YourFilesAreBeingProcessedSequentially")}</p>
                                                        <div className="multi-upload-progress-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                                            {processedFiles.map(file => (
                                                                <Card key={file.id} className="mb-2 shadow-sm">
                                                                    <CardBody className="p-2">
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="flex-shrink-0 me-3 ms-1">
                                                                                {file.status === 'analyzing' && <Spinner size="sm" color="primary" />}
                                                                                {file.status === 'success' && <i className="ri-checkbox-circle-fill text-success fs-4"></i>}
                                                                                {file.status === 'error' && <i className="ri-close-circle-fill text-danger fs-4"></i>}
                                                                                {file.status === 'pending' && <i className="ri-time-line text-muted fs-4"></i>}
                                                                            </div>
                                                                            <div className="flex-grow-1 overflow-hidden">
                                                                                <h6 className="mb-1 text-truncate" title={file.name}>{file.name}</h6>
                                                                                <p className="mb-0 text-muted small">{t(file.message)}</p>
                                                                            </div>
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center p-4">
                                                        <h4 className="mb-3">{t("Analyzing Your Document")}</h4>
                                                        <p className="text-muted mb-4">{t("This may take a moment")}</p>
                                                        <div className="d-flex justify-content-center mb-4">
                                                            <div className="progress-ring" style={{'--progress': `${uploadProgress}%`}}>
                                                                <div className="progress-ring-value">{uploadProgress}%</div>
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 mx-auto" style={{ maxWidth: '400px' }}>
                                                            <ul className={`list-unstyled d-flex flex-column align-items-start`}>
                                                                {processingLog.map((step) => (
                                                                    <li key={step.id} className="d-flex align-items-center mb-2">
                                                                        <i className="ri-check-line text-success me-2 ms-2"></i>
                                                                        <span className="text-muted small">{t(step.text)}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )
                                            ) :  documentUploadError ? (
                                                <div className="text-center p-5">
                                                    <Alert color="danger">
                                                        <i className="ri-error-warning-line fs-1"></i>
                                                        <h5 className="alert-heading mt-2">{t("Analysis Failed")}</h5>
                                                        <p>{t(documentUploadError) || t("An unknown error occurred")}</p>
                                                        <Button color="secondary" onClick={handleClear}>{t("Start over")}</Button>
                                                    </Alert>
                                                </div>
                                            ) : (
                                            <div className="enhanced-filepond-container">
                                             <FilePond
                                                ref={filepondRef}
                                                files={filepondFiles}
                                                onaddfile={handleFilePondAddFile}
                                                onupdatefiles={handleFilePondUpdate}
                                                onwarning={handleFilePondWarning} 
                                                allowMultiple={true}
                                                maxFiles={10}
                                                maxTotalFileSize={`${MAX_FILE_SIZE}B`} 
                                                maxFileSize={`${MAX_FILE_SIZE / (1024 * 1024)}MB`}
                                                name="file"
                                                acceptedFileTypes={['application/pdf', '.pdf']}
                                                // disabled={!areCategoriesSelected || documentUploadLoading || isGeneratingDetails}
                                                disabled={!areCategoriesSelected || isGeneratingDetails}
                                                credits={false}
                                                labelIdle={isRTL ? "اسحب وأفلت ملفاتك أو <span class='filepond--label-action'>استعرض</span>" : "Drag & drop your files or <span class='filepond--label-action'>Browse</span>"}
                                                labelFileProcessing={isRTL ? "جارٍ المعالجة..." : "Processing..."}
                                                labelMaxFileSizeExceeded={isRTL ? "الملف كبير جدًا" : "File is too large"}
                                                labelMaxTotalFileSizeExceeded={isRTL ? "تم تجاوز الحجم الإجمالي للملفات" : "Maximum total file size exceeded"}
                                                labelMaxFileSize={isRTL ? "الحد الأقصى لحجم الملف هو {filesize}" : "Maximum file size is {filesize}"}
                                                labelFileTypeNotAllowed={isRTL ? "نوع الملف غير صالح" : "File is of invalid type"}
                                                labelFileProcessingError={isRTL ? "حدث خطأ أثناء المعالجة" : "Error during processing"}
                                              />
                                            </div>
                                          )}
                                      </div>
                                  </div>
                                </Col>
                            </Row>
                       </Form>
                      </>
                    )}
                </CardBody>
            </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DocumentUploadPage;